import { useState } from 'react';
import { useStore } from '../store/useStore';
import { signOutUser } from '../lib/auth';
import { useT } from '../i18n/useT';
import { Menu, MenuItem, anchorBelow } from './Menu';
import type { MenuAnchor } from './Menu';

const SYNC_KEY: Record<string, string> = {
  off: 'sync.off',
  connecting: 'sync.connecting',
  synced: 'sync.synced',
  offline: 'sync.offline',
  error: 'sync.error',
};

/** Toolbar account control: "Sign in" when signed out, avatar + menu when in. */
export function AccountButton() {
  const t = useT();
  const user = useStore((s) => s.user);
  const authReady = useStore((s) => s.authReady);
  const setAuthOpen = useStore((s) => s.setAuthOpen);
  const setSharesOpen = useStore((s) => s.setSharesOpen);
  const syncStatus = useStore((s) => s.syncStatus);
  const [menu, setMenu] = useState<MenuAnchor | null>(null);

  if (!authReady) return null;

  if (!user) {
    return (
      <button className="mb-btn" onClick={() => setAuthOpen(true)} title={t('acct.signInTitle')}>
        {t('acct.signIn')}
      </button>
    );
  }

  const label = user.displayName || user.email || 'Account';
  const initial = label.slice(0, 1).toUpperCase();

  return (
    <>
      <button
        className="mb-avatar"
        onClick={(e) => {
          const at = anchorBelow(e.currentTarget);
          setMenu((m) => (m ? null : at));
        }}
        title={label}
        aria-label={t('acct.account')}
        aria-haspopup="menu"
        aria-expanded={!!menu}
      >
        {user.photoURL ? (
          <img src={user.photoURL} alt="" referrerPolicy="no-referrer" />
        ) : (
          <span>{initial}</span>
        )}
      </button>
      {menu && (
        <Menu anchor={menu} onClose={() => setMenu(null)} label={t('acct.account')}>
          <div className="mb-account-email">{user.email || user.displayName}</div>
          <div className={`mb-account-sync mb-account-sync-${syncStatus}`}>
            <span className="mb-account-sync-dot" aria-hidden="true" />
            {t(SYNC_KEY[syncStatus] ?? 'sync.off')}
          </div>
          <MenuItem onSelect={() => setSharesOpen(true)}>{t('acct.manageShares')}</MenuItem>
          <MenuItem onSelect={() => useStore.getState().setAccountDialogOpen(true)}>
            {t('acct.accountData')}
          </MenuItem>
          <MenuItem onSelect={() => void signOutUser()}>{t('acct.signOut')}</MenuItem>
        </Menu>
      )}
    </>
  );
}
